import { credentialApi } from '@/api';
import { DatePicker, ModalConfirmation, Select } from '@/components/common';
import { cleanCultureDateFormat } from '@/Helper';
import { User } from '@/model/AccountModel';
import { SelectOptions } from '@/model/CommonModel';
import { RelativeShortcut } from '@/model/EnrollmentModel';
import { Button, Checkbox, Input, Radio, RadioChangeEvent, Space } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import cx from 'classnames';
import { Moment } from 'moment';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { batch } from 'react-redux';
import styles from '../../../credentialmodal.module.scss';
import {
	setGeneralTabActivationExpirationPropertyAction,
	setOptionsTabPropertyAction,
	setRootModelPropertyAction,
} from '../../../credentialTemplateContext/actions';
import { CredentialStoreContext } from '../../../credentialTemplateContext/context';
import { GeneralTabActivationExpirationProperty } from '../../../CredentialTemplateModalModels';
import { generateShortcutDate, getDateISOString, getMomentDate, getRelativeShortcutInfoById, getRelativeTimeShortcutIndex } from '../../../Helper';
import { ExpirationDateModal } from '../ExpirationDateModal/ExpirationDateModal';

const user: User = getUser();
const cultureDateFormat: string = user.cultureDateFormat;

const ActivationExpirationSection: React.FC = () => {
	//#region State Management Variables
	const {
		credentialInitialState: {
			configData: { IsDeviceAdminCredentialTemplate },
			fetchedCredential,
			generalTabState: {
				credentialId,
				informationSectionState: { credentialTemplates, linkToCredential, isLinkToConfirmed },
				activationExpirationSectionState: {
					activationDate,
					expiration,
					expirationFunction,
					expirationUsed,
					hostExpirationUDF,
					hostExpirationInterval,
					hostActivationInterval,
					hostActivationOffset,
				},
			},
			modelEstablished,
			optionsTabState: { disableAction },
			relativeTimeShortcuts,
		},
		dispatcher,
	} = useContext(CredentialStoreContext);

	const dateFormat = cleanCultureDateFormat(cultureDateFormat);

	const [activationDateD, setActivationDateD] = useState(false);
	const [expirationD, setExpirationD] = useState(!expirationUsed);
	const [openExpirationModal, setOpenExpirationModal] = useState(false);
	const [expirationFunctionD, setExpirationFunctionD] = useState(!expirationUsed);
	const [expirationUsedD, setExpirationUsedD] = useState(false);
	const [expiryButtonH, setExpiryButtonH] = useState(true);
	const [expirationUsedChanged, setExpirationUsedChanged] = useState<boolean>(undefined);
	const [mActivationDate, setMActivationDate] = useState<Moment>(getMomentDate(activationDate || '', dateFormat));
	const [mExpiration, setMExpiration] = useState<Moment>(getMomentDate(expiration || '', dateFormat));
	const [relativeShortcutId, setRelativeShortcutId] = useState<string>('');
	const [activationDatePlaceHolder, setActivationDatePlaceHolder] = useState<string>('');
	const firstRender = useRef<boolean>(true);
	const warningShown = useRef<boolean>(false);
	//#endregion

	useEffect(() => {
		const lActivationDate = getMomentDate(activationDate || '', dateFormat).toISOString(true);
		let lExpiration = '';
		if (hostExpirationUDF > 0 || hostExpirationInterval !== 0) {
			lExpiration = expiration;
		} else {
			lExpiration = getMomentDate(expiration || '', dateFormat).toISOString(true);
		}
		dispatcher(setGeneralTabActivationExpirationPropertyAction({ activationDate: lActivationDate, expiration: lExpiration }));
	}, []);

	useEffect(() => {
		if (!firstRender.current) {
			expirationChanged();
		} else {
			firstRender.current = false;
		}
	}, [expirationUsed]);

	useEffect(() => {
		if (isLinkToConfirmed !== undefined) linkToChkChanged();
	}, [isLinkToConfirmed]);

	useEffect(() => {
		if (fetchedCredential && modelEstablished) {
			linkToChkChanged();
			applyActivationDateOption(relativeTimeShortcuts, activationDate, hostActivationInterval, hostActivationOffset, IsDeviceAdminCredentialTemplate);
		}
	}, [fetchedCredential, modelEstablished]);

	useEffect(() => {
		setMActivationDate(getMomentDate(activationDate || '', dateFormat));
		setMExpiration(getMomentDate(expiration || '', dateFormat));
		expirationChanged();
	}, [activationDate, expiration]);

	const linkToChkChanged = () => {
		if (credentialTemplates?.length > 0) {
			if (linkToCredential) {
				toggleDisableAll(true);
			} else {
				toggleDisableAll(false);
				expirationChanged(true);
			}
		}
	};

	const handleChangeGeneralTabProperty = (value: GeneralTabActivationExpirationProperty) => {
		dispatcher(setGeneralTabActivationExpirationPropertyAction(value));
		dispatcher(setRootModelPropertyAction({ hasChanged: true }));
	};

	const showDisableCredentialConfirmation = () => {
		warningShown.current = true;
		ModalConfirmation({
			cancelText: _('No'),
			okText: _('Yes'),
			width: '600px',
			content: (
				<div data-test-id='DisabledCredentialWarningModal'>
					<label htmlFor=''>{`${_('DisableCredentialOption')} `}</label>
					<label htmlFor=''>{_('DoYouWantToReEnableCredential')}</label>
				</div>
			),
			onConfirm: () => {
				batch(() => {
					dispatcher(setRootModelPropertyAction({ hasChanged: true }));
					dispatcher(setOptionsTabPropertyAction({ disableAction: false }));
				});
				warningShown.current = false;
			},
			onCancel: () => (warningShown.current = false),
		});
	};

	const expirationChanged = (linkToChange?: boolean /* , isLinkedTo?: boolean*/) => {
		if (expirationUsed) {
			// const booleanIsLinkedTo = linkToCredential ? true : false;
			setExpirationFunctionD(linkToCredential);

			if (IsDeviceAdminCredentialTemplate || hostExpirationUDF !== 0) {
				setExpirationD(true);
			} else {
				setExpirationD(linkToCredential);
			}
			if (!linkToCredential) setExpiryButtonH(false);
		} else {
			setExpirationFunctionD(true);
			setExpirationD(true);
			setExpiryButtonH(true);

			if (!linkToChange && disableAction && !IsDeviceAdminCredentialTemplate && !warningShown.current) {
				showDisableCredentialConfirmation();
			}
		}
	};

	const toggleDisableAll = (disabled: boolean) => {
		setActivationDateD(disabled);
		setExpirationD(disabled);
		setExpirationFunctionD(disabled);
		setExpirationUsedD(disabled);
		setExpiryButtonH(disabled);
	};

	const showCredentialTemplateExpirationConfirmation = () => {
		ModalConfirmation({
			cancelText: _('No'),
			okText: _('Yes'),
			width: '600px',
			content: (
				<div>
					<label htmlFor=''>{`${_('CredentialTemplateExpirationConfirmation')} `}</label>
					<label htmlFor=''>{_('DoYouWantToProceed')}</label>
				</div>
			),
			onConfirm: () => setOpenExpirationModal(true),
		});
	};

	const handleOnClickExpireButton = async () => {
		if (IsDeviceAdminCredentialTemplate) {
			const linkedCredentialCount = await credentialApi.getDeactivatedLinkCredentialCount(credentialId);

			if (linkedCredentialCount === 0) {
				setOpenExpirationModal(true);
			} else {
				showCredentialTemplateExpirationConfirmation();
			}
		} else {
			setOpenExpirationModal(true);
		}
	};

	const handleOnChangeExpirationDate = (date: Moment) => {
		handleChangeGeneralTabProperty({ expiration: date ? date.toISOString(true) : null });
	};

	const handleOnExpirationUsedChanged = (e: CheckboxChangeEvent) => {
		setExpirationUsedChanged(!expirationUsedChanged);
		handleChangeGeneralTabProperty({ expirationUsed: e.target.checked });
	};

	const selectDateOptions = useMemo(
		() =>
			relativeTimeShortcuts.map<SelectOptions<string>>((item, index) => ({
				label: item.Display,
				value: item.Index.toString(),
				id: `relativeShortcutsDropdownOption-${index}`,
			})),
		[relativeTimeShortcuts]
	);

	const handleOnChangeActivationShortcutDate = value => {
		const activationShortcutId: number = Number(value);
		const relativeShortcut: RelativeShortcut = relativeTimeShortcuts?.find(x => x.Index === activationShortcutId);
		let customDate: string;
		if (relativeShortcut) {
			customDate = getDateISOString(generateShortcutDate(relativeShortcut.Interval, relativeShortcut.OffSetOrTime));
		} else {
			customDate = getDateISOString(generateShortcutDate(activationShortcutId, null));
		}

		if (IsDeviceAdminCredentialTemplate && relativeShortcut) {
			const shortcutInfo = getRelativeShortcutInfoById(relativeTimeShortcuts, activationShortcutId);

			const activationExpirationProps: GeneralTabActivationExpirationProperty = {
				hostActivationInterval: shortcutInfo.Interval,
				hostActivationOffset: shortcutInfo.OffSetOrTime,
			};

			dispatcher(setGeneralTabActivationExpirationPropertyAction(activationExpirationProps));
			setActivationDatePlaceHolder(relativeShortcut.Display);
		} else {
			handleChangeGeneralTabProperty({ activationDate: customDate });
		}

		setRelativeShortcutId(value);
	};

	const intervalNameFormat = value => activationDatePlaceHolder;

	const applyActivationDateOption = (
		relativeShortcuts: RelativeShortcut[],
		activationDate: string,
		hostActivationInterval: number,
		hostActivationOffset: string,
		isDeviceAdminCredentialTemplate: boolean
	) => {
		if (hostActivationInterval > 0) {
			const relativeShortcutIndex = getRelativeTimeShortcutIndex(hostActivationInterval, hostActivationOffset, relativeShortcuts);
			if (relativeShortcutIndex !== -1) {
				const relativeShortcut = relativeShortcuts[relativeShortcutIndex];
				setRelativeShortcutId(relativeShortcut.Index.toString());
				activationDate = getDateISOString(generateShortcutDate(hostActivationInterval, relativeShortcut.OffSetOrTime));
				if (isDeviceAdminCredentialTemplate) {
					setActivationDatePlaceHolder(relativeShortcut.Display);
				}
			}
		}

		dispatcher(setGeneralTabActivationExpirationPropertyAction({ activationDate: activationDate }));
		if (!isDeviceAdminCredentialTemplate) {
			batch(() => {
				setActivationDatePlaceHolder('');
				setRelativeShortcutId('');
			});
		}
	};

	const handleOnChangeDatePicker = (momentDate: Moment) => {
		if (IsDeviceAdminCredentialTemplate) {
			const activationExpirationProps: GeneralTabActivationExpirationProperty = {
				hostActivationInterval: 0,
				hostActivationOffset: '',
			};

			batch(() => {
				setActivationDatePlaceHolder('');
				setRelativeShortcutId('');
				dispatcher(setGeneralTabActivationExpirationPropertyAction(activationExpirationProps));
			});
		}

		handleChangeGeneralTabProperty({ activationDate: momentDate ? momentDate.toISOString(true) : null });
	};

	const activationDateFormat = IsDeviceAdminCredentialTemplate && activationDatePlaceHolder ? intervalNameFormat : dateFormat;

	return (
		<>
			<div className={cx(styles.containerLegend, styles.rightContainerLegend)}>
				<fieldset className={styles.fieldset}>
					<legend className={styles.legend}>{`${_('Activation')}/${_('ExpirationDate')}`}</legend>
					<div className={styles.form}>
						<div className={styles.dateFields}>
							<label htmlFor='activationDate'>{_('Activate')}:</label>
							<DatePicker
								allowClear={false}
								className={styles.input}
								disabled={activationDateD}
								format={activationDateFormat}
								getPopupContainer={(triggerElement: HTMLElement) => triggerElement.parentElement}
								id='activationDate'
								name='activationDate'
								onChange={handleOnChangeDatePicker}
								showTime
								value={mActivationDate}
								placeholder={activationDatePlaceHolder}
							/>
							<Select
								id='activationRelativeTimeSelect'
								options={selectDateOptions}
								value={relativeShortcutId}
								onChange={handleOnChangeActivationShortcutDate}
								className={styles.activationRelativeTime}
								showSearch={true}
								maxSearchLength={50}
								disabled={activationDateD}
							/>
						</div>
						<div className={styles.dateFields}>
							<Checkbox
								checked={expirationUsed}
								disabled={expirationUsedD}
								id='expirationUsed'
								name='ExpirationUsed'
								onChange={handleOnExpirationUsedChanged}>
								{_('Expire')}
							</Checkbox>
							{hostExpirationUDF > 0 || hostExpirationInterval !== 0 ? (
								<Input
									className={styles.input}
									id={hostExpirationInterval !== 0 ? 'expirationOnShortcut' : 'expirationOnUDF'}
									maxLength={50}
									name='expirationOnUDF'
									disabled
									value={expiration}
								/>
							) : (
								<DatePicker
									allowClear={false}
									aria-label={_('Expiration')}
									className={styles.input}
									disabled={expirationD}
									format={dateFormat}
									getPopupContainer={(triggerElement: HTMLElement) => triggerElement.parentElement}
									id='expiration'
									name='expiration'
									onChange={handleOnChangeExpirationDate}
									showTime
									value={mExpiration}
								/>
							)}
							<Button
								className={cx(styles.expirationRelativeTime, { [styles.hidden]: expiryButtonH })}
								htmlType='button'
								id='ExpiryButton'
								onClick={handleOnClickExpireButton}>
								...
							</Button>
						</div>
						<div className={styles.activationDateSections}>
							<div></div>
							<div>
								<label>{`${_('OnExpiration')}:`}</label>
								<div className={styles.onExpiration}>
									<Radio.Group
										defaultValue={0}
										disabled={expirationFunctionD}
										id='expirationFunction'
										name='expirationFunction'
										onChange={(e: RadioChangeEvent) => {
											handleChangeGeneralTabProperty({ expirationFunction: e.target.value });
										}}
										value={expirationFunction}>
										<Space direction='vertical'>
											<Radio value={0}>{_('Disable')}</Radio>
											<Radio value={1}>{_('DeleteFromControllers')}</Radio>
											<Radio value={3}>{_('DisableAndUnassign')}</Radio>
										</Space>
									</Radio.Group>
								</div>
							</div>
						</div>
					</div>
				</fieldset>
			</div>
			{openExpirationModal && <ExpirationDateModal onCloseModal={() => setOpenExpirationModal(false)} />}
		</>
	);
};

export default ActivationExpirationSection;
